import type { B2BPasswordStrengthCheckResponse } from '@stytch/vanilla-js/b2b';
import type { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';

type Strings = ReturnType<typeof useLocalizedStrings>;

// Turns a Stytch strength-check rejection into the list of things the user has to actually change.
//
// This used to live inside Invite.tsx, so the create-password screen explained itself properly and the reset
// screen — which had its own one-liner reading zxcvbn_feedback.suggestions[0] — said nothing at all. Shared so
// the two screens cannot drift again.
//
// Stytch populates only the feedback block matching the project's strength_policy: under a LUDS policy
// zxcvbn_feedback comes back empty, and under a zxcvbn policy luds_feedback does. The response type declares
// both as non-optional, which is why every read below is still guarded.
export function buildPasswordFeedbackMessages(
    response: B2BPasswordStrengthCheckResponse,
    strings: Strings
): string[] {
    const messages: string[] = [];

    if (response.valid_password) {
        return [];
    }

    if (response.breached_password) {
        messages.push(strings.validationPasswordDataBreach);
    }

    const zxcvbn = response.zxcvbn_feedback;

    if (zxcvbn?.warning) {
        messages.push(zxcvbn.warning);
    }

    if (zxcvbn?.suggestions?.length) {
        messages.push(...zxcvbn.suggestions);
    }

    const luds = response.luds_feedback;

    if (luds) {
        if (typeof luds.missing_characters === "number" && luds.missing_characters > 0) {
            messages.push(strings.validationPasswordLength.replace('{{missing}}', luds.missing_characters.toString()));
        }

        if (typeof luds.missing_complexity === "number" && luds.missing_complexity > 0) {
            messages.push(strings.validationPasswordComplexity.replace('{{complexity}}', luds.missing_complexity.toString()));
        }

        if (luds.has_upper_case === false) {
            messages.push(strings.validationPasswordUppercase);
        }

        if (luds.has_lower_case === false) {
            messages.push(strings.validationPasswordLowercase);
        }

        if (luds.has_digit === false) {
            messages.push(strings.valitationPasswordNumber);
        }

        if (luds.has_symbol === false) {
            messages.push(strings.valitationPasswordSymbol);
        }
    }

    // Stytch can repeat itself across the zxcvbn and LUDS blocks; the user only needs to be told once.
    return [...new Set(messages)];
}
